import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** Generic searchable select.
 * options: [{ value: string, label: string, sub?: string }]
 * value:   the selected value (string) — coerce numbers/ints upstream
 * onChange(value): fires with new value (empty string clears)
 */
export default function Combobox({
  options = [], value = "", onChange, placeholder = "Select…",
  emptyText = "No matches", searchPlaceholder = "Search…", className = "",
  testid, allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => String(o.value) === String(value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className={cn("h-9 justify-between font-normal text-sm w-full", className)}
        >
          <span className={cn("truncate text-left", !selected && "text-slate-400")}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronsUpDown size={12} className="shrink-0 opacity-50 ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] z-50" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__clear__"
                  onSelect={() => { onChange(""); setOpen(false); }}
                  className="text-slate-500 italic"
                >
                  Clear selection
                </CommandItem>
              )}
              {options.map((opt) => (
                <CommandItem
                  key={String(opt.value)}
                  value={`${opt.label} ${opt.sub || ""}`}   // search-key
                  onSelect={() => { onChange(String(opt.value)); setOpen(false); }}
                >
                  <Check size={12} className={cn("mr-2", String(value) === String(opt.value) ? "opacity-100" : "opacity-0")} />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{opt.label}</div>
                    {opt.sub && <div className="text-[10px] text-slate-400 truncate">{opt.sub}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
